import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Form,
  Image,
  InputGroup,
  Modal,
  Spinner,
  Table,
} from "react-bootstrap";
import { FaEdit, FaExternalLinkAlt, FaEye, FaPlus, FaSearch, FaTrashAlt, FaImage } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { advertisementsAPI } from "../../api/apiClient";
import PaginationComponent from "../common/PaginationComponent";

const INITIAL_FORM = {
  title: "",
  description: "",
  url: "",
  image: null,
  category_id: "",
};

export default function Advertisements() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [advertisements, setAdvertisements] = useState([]);
  const [filteredAdvertisements, setFilteredAdvertisements] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");
  const [form, setForm] = useState(INITIAL_FORM);
  const [editingAdvertisement, setEditingAdvertisement] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  // جلب التصنيفات
  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await advertisementsAPI.allCategories();
      if (response.success) {
        setCategories(response.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching categories:", err);
    } finally {
      setLoadingCategories(false);
    }
  };

  useEffect(() => {
    fetchAdvertisements();
    fetchCategories();
  }, []);

  useEffect(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) {
      setFilteredAdvertisements(advertisements);
      return;
    }

    setFilteredAdvertisements(
      advertisements.filter(
        (advertisement) =>
          advertisement.title?.toLowerCase().includes(normalizedSearch) ||
          advertisement.description?.toLowerCase().includes(normalizedSearch) ||
          advertisement.category_name?.toLowerCase().includes(normalizedSearch),
      ),
    );
  }, [advertisements, searchTerm]);

  const fetchAdvertisements = async (page = 1) => {
    setLoading(true);
    setError("");
    try {
      const response = await advertisementsAPI.index({
        page,
        per_page: pagination.per_page,
      });

      if (response.success) {
        const items = response.data.data || [];
        setAdvertisements(items);
        setFilteredAdvertisements(items);
        setPagination(
          response.data.meta || {
            current_page: page,
            last_page: 1,
            per_page: 10,
            total: items.length,
          },
        );
      } else {
        setError(response.message || t("advertisements.fetch_failed"));
      }
    } catch (err) {
      console.error("Error fetching advertisements:", err);
      setError(t("advertisements.fetch_failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      const file = files[0];
      setImageFile(file);
      setRemoveImage(false);
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setForm((prev) => ({ ...prev, image: reader.result }));
        };
        reader.readAsDataURL(file);
      } else {
        setForm((prev) => ({ ...prev, image: null }));
      }
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const openCreateModal = () => {
    setEditingAdvertisement(null);
    setForm(INITIAL_FORM);
    setImageFile(null);
    setRemoveImage(false);
    if (fileRef.current) fileRef.current.value = "";
    setShowFormModal(true);
  };

  const openEditModal = (advertisement) => {
    setEditingAdvertisement(advertisement);
    setForm({
      title: advertisement.title || "",
      description: advertisement.description || "",
      url: advertisement.url || "",
      image: advertisement.image || null,
      category_id: advertisement.category_id || "",
    });
    setImageFile(null);
    setRemoveImage(false);
    if (fileRef.current) fileRef.current.value = "";
    setShowFormModal(true);
  };

  const resetFileInput = () => {
    setImageFile(null);
    setRemoveImage(true);
    setForm((prev) => ({ ...prev, image: null }));
    if (fileRef.current) fileRef.current.value = "";
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    setEditingAdvertisement(null);
    setForm(INITIAL_FORM);
    setImageFile(null);
    setRemoveImage(false);
    setError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");

    // التحقق من الحقول المطلوبة
    if (!form.title?.trim()) {
      setError(t("advertisements.title_required"));
      setSaving(false);
      return;
    }
    if (!form.description?.trim()) {
      setError(t("advertisements.description_required"));
      setSaving(false);
      return;
    }
    if (!form.category_id) {
      setError(t("advertisements.category_required"));
      setSaving(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("title", form.title.trim());
      formData.append("description", form.description.trim());
      formData.append("url", form.url?.trim() || "");
      formData.append("category_id", form.category_id);

      // إضافة الصورة إذا كانت موجودة
      if (imageFile) {
        formData.append("image", imageFile);
      } else if (removeImage) {
        formData.append("remove_image", "true");
      }

      let response;
      if (editingAdvertisement) {
        // formData.append("_method", "PUT");
        response = await advertisementsAPI.update(editingAdvertisement.id, formData);
      } else {
        response = await advertisementsAPI.store(formData);
      }

      if (!response.success) {
        setError(response.message || t("advertisements.save_failed"));
        setSaving(false);
        return;
      }

      closeFormModal();
      await fetchAdvertisements(pagination.current_page);
    } catch (err) {
      console.error("Error saving advertisement:", err);
      setError(t("advertisements.save_failed"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (advertisement) => {
    const confirmed = await Swal.fire({
      title: t("advertisements.delete_confirm"),
      text: t("advertisements.confirm_delete", { title: advertisement.title }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("advertisements.delete"),
      cancelButtonText: t("advertisements.cancel"),
    });

    if (!confirmed.value) return;

    try {
      const response = await advertisementsAPI.delete(advertisement.id);
      if (!response.success) {
        setError(response.message || t("advertisements.delete_failed"));
        return;
      }
      await fetchAdvertisements(pagination.current_page);
    } catch (err) {
      console.error("Error deleting advertisement:", err);
      setError(t("advertisements.delete_failed"));
    }
  };

  const handlePageChange = (page) => {
    if (page !== pagination.current_page) {
      fetchAdvertisements(page);
    }
  };

  // عرض حالة التحميل
  if (loading && advertisements.length === 0) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">{t("advertisements.loading")}</p>
      </div>
    );
  }

  // الحصول على اسم التصنيف من المعرف
  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : "Uncategorized";
  };

  return (
    <div className="py-4">
      <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
        <h3 className="fw-bold mb-3">{t("advertisements.title")}</h3>
        <Button variant="primary" onClick={openCreateModal} className="rounded-pill px-4">
          <FaPlus className="me-2" />
          {t("advertisements.add")}
        </Button>
      </div>

      <InputGroup className="mb-3">
        <InputGroup.Text className="bg-white">
          <FaSearch className="text-muted" />
        </InputGroup.Text>
        <Form.Control
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder={t("advertisements.search_placeholder")}
          className="border-start-0"
        />
        {searchTerm && (
          <Button
            variant="outline-secondary"
            onClick={() => setSearchTerm("")}
            className="border"
          >
            ✕
          </Button>
        )}
      </InputGroup>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <div className="table-responsive bg-white rounded shadow-sm">
        <Table bordered hover className="mb-0 text-center align-middle">
          <thead className="table-dark">
            <tr>
              <th style={{ width: "50px" }}>#</th>
              <th>{t("advertisements.title")}</th>
              <th>{t("advertisements.description")}</th>
              <th>{t("advertisements.category")}</th>
              <th>{t("advertisements.image")}</th>
              <th>{t("advertisements.url")}</th>
              {/* <th>{t("advertisements.status")}</th> */}
              <th style={{ width: "160px" }}>{t("advertisements.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {filteredAdvertisements.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-4 text-muted">
                  {searchTerm ? t("advertisements.no_results") : t("advertisements.no_data")}
                </td>
              </tr>
            ) : (
              filteredAdvertisements.map((advertisement, index) => (
                <tr key={advertisement.id}>
                  <td>{(pagination.current_page - 1) * pagination.per_page + index + 1}</td>
                  <td className="fw-bold">{advertisement.title}</td>
                  <td className="text-start" style={{ maxWidth: "200px" }}>
                    <span className="text-truncate d-block">
                      {advertisement.description}
                    </span>
                  </td>
                  <td>
                    <Badge bg="info" className="px-3 py-2">
                      {t(`advertisements.tabs.${advertisement.category_name}`) || getCategoryName(advertisement.category_id) || "Uncategorized"}
                    </Badge>
                  </td>
                  <td>
                    {advertisement.image ? (
                      <img
                        src={advertisement.image}
                        alt={advertisement.title}
                        style={{
                          width: "60px",
                          height: "60px",
                          objectFit: "cover",
                          borderRadius: "8px",
                          border: "1px solid #eee",
                        }}
                        onError={(e) => {
                          e.target.src = "/placeholder-image.png";
                        }}
                      />
                    ) : (
                      <div
                        className="d-flex align-items-center justify-content-center bg-light"
                        style={{
                          width: "60px",
                          height: "60px",
                          borderRadius: "8px",
                          margin: "0 auto",
                        }}
                      >
                        <FaImage className="text-muted" size={24} />
                      </div>
                    )}
                  </td>
                  <td>
                    {advertisement.url ? (
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={() => window.open(advertisement.url, "_blank")}
                        className="rounded-pill px-3 d-flex align-items-center justify-content-center m"
                      >
                        <FaExternalLinkAlt className="me-1" size={12} />
                        {t("advertisements.visit")}
                      </Button>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  {/* <td>
                    <Badge bg={advertisement.is_active ? "success" : "secondary"}>
                      {advertisement.is_active ? t("advertisements.active") : t("advertisements.inactive")}
                    </Badge>
                    {advertisement.views_count > 0 && (
                      <Badge bg="light" text="dark" className="ms-1">
                        <FaEye size={10} className="me-1" />
                        {advertisement.views_count}
                      </Badge>
                    )}
                  </td> */}
                  <td>
                    <div className="d-flex justify-content-center gap-1 flex-wrap">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => openEditModal(advertisement)}
                        title={t("advertisements.edit")}
                      >
                        <FaEdit />
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(advertisement)}
                        title={t("advertisements.delete")}
                      >
                        <FaTrashAlt />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {!loading && pagination.total > 0 && (
        <PaginationComponent
          currentPage={pagination.current_page}
          lastPage={pagination.last_page}
          onPageChange={handlePageChange}
          disabled={loading}
        />
      )}

      {/* Create/Update Modal */}
      <Modal show={showFormModal} onHide={closeFormModal} centered size="lg">
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton className="bg-dark text-white">
            <Modal.Title>
              {editingAdvertisement ? t("advertisements.edit") : t("advertisements.add")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError("")}>
                {error}
              </Alert>
            )}

            {/* عرض الصورة الحالية */}
            {form.image && (
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">{t("advertisements.current_image")}</Form.Label>
                <div>
                  <img
                    src={form.image}
                    alt={t("advertisements.current_image")}
                    style={{
                      width: "120px",
                      height: "120px",
                      objectFit: "cover",
                      borderRadius: "8px",
                      border: "1px solid #ddd",
                    }}
                    onError={(e) => {
                      e.target.src = "/placeholder-image.png";
                    }}
                  />
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={resetFileInput}
                    className="mt-2 d-block"
                  >
                    {t("advertisements.remove_image")}
                  </Button>
                </div>
              </Form.Group>
            )}

            {/* رفع صورة جديدة */}
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">{t("advertisements.image")} <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="file"
                name="image"
                ref={fileRef}
                onChange={handleInputChange}
                accept="image/*"
                required={!form.image} // مطلوب إذا لم يكن هناك صورة حالية أو صورة جديدة
              />
              <Form.Text className="text-muted">
                {t("advertisements.image_hint")}
              </Form.Text>
            </Form.Group>

            {/* Dropdown للتصنيفات */}
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">
                {t("advertisements.category")} <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                name="category_id"
                value={form.category_id}
                onChange={handleInputChange}
                required
                disabled={loadingCategories}
              >
                <option value="">{t("advertisements.select_category")}</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {t(`advertisements.tabs.${category.name}`) || category.name}
                  </option>
                ))}
              </Form.Select>
              {loadingCategories && (
                <div className="mt-1">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2 text-muted">{t("advertisements.loading_categories")}</span>
                </div>
              )}
              <Form.Text className="text-muted">
                {t("advertisements.category_hint")}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">
                {t("advertisements.title")} <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                name="title"
                value={form.title}
                onChange={handleInputChange}
                placeholder={t("advertisements.title_placeholder")}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">
                {t("advertisements.description")} <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={form.description}
                onChange={handleInputChange}
                placeholder={t("advertisements.description_placeholder")}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">
                {t("advertisements.url")} <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                name="url"
                value={form.url}
                onChange={handleInputChange}
                placeholder="https://example.com"
                type="url"
                required
              />
              <Form.Text className="text-muted">
                {t("advertisements.url_hint")}
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeFormModal}>
              {t("advertisements.cancel")}
            </Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {t("advertisements.saving")}
                </>
              ) : editingAdvertisement ? (
                t("advertisements.update")
              ) : (
                t("advertisements.create")
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}